import { ApiError, NetworkError } from '@/utils/errors';

/**
 * 端到端结果表——后端返回的 code 最终会让用户看到什么。这张表描述的是
 * ACTIONS/DEFAULT_ACTION 本身，即「classify() 单独被调用一次」的结果；
 * SESSION_NOT_FOUND 有一行例外，下面单独说明，其它行都是 useChatSession
 * 直接照抄 classify() 的结果去渲染。
 *
 * - SESSION_NOT_FOUND：banner=false, failedBubble=false ——
 *   无感：丢弃 session_id，静默重发一次，用户什么都看不到。
 * - UNAUTHORIZED：banner=true, failedBubble=false ——
 *   只有底部 session banner，不给重试入口。
 * - RATE_LIMITED：banner=true, failedBubble=true —— 两个都出：
 *   session banner **和**一条可重试的失败气泡同时出现，不是二选一。
 * - INVALID_REQUEST：banner=false, failedBubble=true ——
 *   失败气泡，不可重试（retriable=false）。
 * - 其它已知/未知 code、网络故障、超时（DEFAULT_ACTION）：
 *   banner=false, failedBubble=true —— 失败气泡，可重试。
 *
 * SESSION_NOT_FOUND 的例外：上面那一行只在「第一次出现、静默重试即将
 * 发生」这一刻成立。如果静默重试已经用过一次、这次还是 SESSION_NOT_FOUND
 * （或者调用方一开始就不允许静默重试），useChatSession.ts 的
 * handleFailure() 不会照抄这一行——它会整个改写成一个合成的
 * `ApiError(0, 'INTERNAL_ERROR', ...)`：最终结果是一条可重试的失败气泡，
 * 不是「什么都不显示」。想知道 SESSION_NOT_FOUND 真正的端到端行为，去看
 * handleFailure 的实现和它顶部的注释，不要只读这张表。
 */

/**
 * 一次失败之后 UI 该做什么。四个布尔量彼此正交，方便逐项断言。
 */
export interface ErrorAction {
  /** 丢弃 session_id 并不带 id 自动重发一次，全程不给用户任何提示。 */
  silentNewSession: boolean;
  /** 在 composer 上方显示会话级 banner（限流倒计时、鉴权失效）。 */
  banner: boolean;
  /** 在对话流里、失败的那条用户消息后面插一条失败气泡。 */
  failedBubble: boolean;
  /** 失败气泡上是否给重试按钮。 */
  retriable: boolean;
}

/**
 * 取出后端的错误码。非流式来自 HTTP body，流式来自 SSE error 事件——
 * 两条路径都由调用方包成 ApiError，见 useChatSession。
 */
export function getErrorCode(error: unknown): string | undefined {
  if (error instanceof ApiError) return error.code;
  if (error instanceof NetworkError) return 'NETWORK_ERROR';
  return undefined;
}

const ACTIONS: Record<string, ErrorAction> = {
  SESSION_NOT_FOUND: {
    silentNewSession: true,
    banner: false,
    failedBubble: false,
    retriable: false,
  },
  UNAUTHORIZED: { silentNewSession: false, banner: true, failedBubble: false, retriable: false },
  RATE_LIMITED: { silentNewSession: false, banner: true, failedBubble: true, retriable: true },
  INVALID_REQUEST: {
    silentNewSession: false,
    banner: false,
    failedBubble: true,
    retriable: false,
  },
};

const DEFAULT_ACTION: ErrorAction = {
  silentNewSession: false,
  banner: false,
  failedBubble: true,
  retriable: true,
};

/**
 * 未知 code、网络故障、超时一律按「可重试的失败气泡」处理——用户至少
 * 有一个明确的下一步动作。
 */
export function classify(error: unknown): ErrorAction {
  const code = getErrorCode(error);
  return (code && ACTIONS[code]) || DEFAULT_ACTION;
}

/**
 * 会话作用域 GET 的「确定性拒绝」：再拉一次结果不会变，这个 sid 还原不出
 * 历史。只用在历史 GET（GET /api/chat/sessions/{sid}/messages）上——那是
 * 一个 session 作用域的 URL，404 无论出自「会话不存在」「会话被清理」还是
 * 「这个部署没有这条路由」，结论都一样：原样重发同一个 GET 永远不会成功。
 *
 * 首选后端 envelope（SESSION_NOT_FOUND），但不依赖它——openapi.json 对这条
 * 路由只承诺过 {200, 422}，压根没声明过错误 envelope 的形状。把正确性押在
 * 一个从未被承诺过的 body 字段上，等于让一个行为完全合理的后端（比如只是
 * 没把这条路由部署到这个环境）也能把前端永久卡在一条「重试」永远失败的
 * banner 上。
 *
 * 不要挪去用在 POST /api/chat/messages 上：那条路由不是 session 作用域的
 * （sid 在 body 里，不在 URL 里），它的 404 只意味着「没有这条路由」——
 * 用这个函数的结论去丢 session_id，会把一段后端其实还留着的对话变成孤儿。
 */
export function isRestoreHopeless(error: unknown): boolean {
  if (getErrorCode(error) === 'SESSION_NOT_FOUND') return true;
  if (!(error instanceof ApiError)) return false; // NetworkError/TimeoutError → 可重试
  const status = error.status;
  if (status === 401 || status === 403 || status === 408 || status === 429) return false; // 会话级/暂时性 → banner
  return status >= 400 && status < 500;
}
